/**
 * Product Siblings Variants
 * Handles color swatch clicks for sibling products
 */

class ProductSiblingsVariants {
  constructor(container) {
    this.container = container;
    this.section = container.closest('[data-section-type]') || container.closest('[data-section-id]');
    this.productForm = document.querySelector('product-form');
    this.isUpdating = false;
    
    this.init();
  }

  init() {
    this.attachEventListeners();
    this.updateActiveStates();
    this.hideUnavailableColors();
    // Disabled: do not auto-redirect to an available variant on load
    // this.redirectToAvailableVariant();
    this.updateInitialSizeAvailability();
    this.updateInitialVariantUI();
  }

  attachEventListeners() {
    // Handle color swatch clicks
    this.container.addEventListener('click', (e) => {
      const swatch = e.target.closest('[data-sibling-color-swatch]');
      if (swatch && !this.isUpdating) {
        e.preventDefault();
        e.stopPropagation();
        this.handleColorSwatchClick(swatch);
      }
    });
    
    // Handle size selector changes (only update price/sku, not availability)
    const sizeContainer = this.section.querySelector('[data-sibling-sizes]');
    if (sizeContainer) {
      sizeContainer.addEventListener('change', (e) => {
        const sizeInput = e.target.closest('[data-single-option-selector]');
        if (sizeInput && !this.isUpdating) {
          console.log('[Siblings] Size changed to:', sizeInput.value);
          // Prevent the theme's applyHide from running
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.handleSizeChange(sizeInput.value);
        }
      }, true); // Use capture phase to intercept before theme handlers
    }
  }

  handleColorSwatchClick(swatch) {
    const productId = swatch.dataset.siblingProductId;
    const productUrl = swatch.dataset.siblingProductUrl;
    const colorValue = swatch.dataset.colorValue;
    const input = swatch.querySelector('input[data-sibling-color-input]');
    
    if (!input || input.disabled) return;
    
    const parentWrapper = swatch.closest('[data-sibling-product]');
    const isCurrentProduct = parentWrapper?.hasAttribute('data-current-product');
    
    if (isCurrentProduct) {
      // Update current product variant
      input.checked = true;
      this.updateMainProductVariant(colorValue);
    } else {
      // Navigate to sibling product
      const siblingVariantId = this.findSiblingVariant(productId, colorValue);
      const url = siblingVariantId ? this.getUrlWithVariant(productUrl, siblingVariantId) : productUrl;
      window.location.href = url;
    }
  }

  handleSizeChange(newSize) {
    this.isUpdating = true;
    
    // Save current state of all size buttons (display, disabled, classes)
    const sizeButtons = this.section.querySelectorAll('[data-sibling-sizes] .radio__button');
    const savedStates = new Map();
    
    sizeButtons.forEach(button => {
      const input = button.querySelector('[data-single-option-selector]');
      if (input) {
        savedStates.set(input, {
          disabled: input.disabled,
          classList: Array.from(input.classList),
          buttonDisplay: button.style.display
        });
      }
    });
    
    // Get product data
    const productDataElement = document.querySelector('[data-product-json]');
    if (!productDataElement) {
      this.isUpdating = false;
      return;
    }
    
    const productData = JSON.parse(productDataElement.textContent);
    
    // Get current color from a checked color swatch
    const checkedColorSwatch = this.container.querySelector('[data-sibling-color-swatch] input[data-sibling-color-input]:checked');
    const currentColor = checkedColorSwatch?.value;
    
    if (!currentColor) {
      this.isUpdating = false;
      return;
    }
    
    // Find matching variant (color + size)
    const matchingVariant = productData.variants.find(v => {
      return v.option1 === currentColor && v.option2 === newSize;
    });
    
    if (matchingVariant) {
      // Update variant ID input
      const variantIdInput = this.section.querySelector('input[name="id"]');
      if (variantIdInput) {
        variantIdInput.value = matchingVariant.id;
      }
      
      // Update URL
      if (window.history.replaceState) {
        const newUrl = this.getUrlWithVariant(window.location.href, matchingVariant.id);
        window.history.replaceState({path: newUrl}, '', newUrl);
      }
      
      // Update product info but NOT images or size availability
      this.updateProductDOM(matchingVariant, productData, false);
      
      // Restore the saved states of all size buttons
      savedStates.forEach((state, input) => {
        const button = input.closest('.radio__button');
        if (button) {
          input.disabled = state.disabled;
          input.className = state.classList.join(' ');
          button.style.display = state.buttonDisplay;
        }
      });
      
      console.log('[Siblings] Restored size button states after size change');
    }
    
    setTimeout(() => { this.isUpdating = false; }, 100);
  }

  updateMainProductVariant(colorValue) {
    this.isUpdating = true;
    
    // Get product data
    const productDataElement = document.querySelector('[data-product-json]');
    if (!productDataElement) {
      this.isUpdating = false;
      return;
    }
    
    const productData = JSON.parse(productDataElement.textContent);
    
    // Get current size
    const sizeSelector = this.section.querySelector('[data-sibling-sizes] [data-single-option-selector]:checked') ||
                        this.section.querySelector('[data-sibling-sizes] [data-single-option-selector][type="hidden"]');
    const currentSize = sizeSelector?.value;
    
    // Find matching variant (color + size)
    const matchingVariant = productData.variants.find(v => {
      return v.option1 === colorValue && (v.option2 === currentSize || !currentSize);
    });
    
    if (matchingVariant) {
      // Update variant ID input
      const variantIdInput = this.section.querySelector('input[name="id"]');
      if (variantIdInput) {
        variantIdInput.value = matchingVariant.id;
      }
      
      // Update URL
      if (window.history.replaceState) {
        const newUrl = this.getUrlWithVariant(window.location.href, matchingVariant.id);
        window.history.replaceState({path: newUrl}, '', newUrl);
      }
      
      // Update DOM with color change flag
      this.updateProductDOM(matchingVariant, productData, true);
    }
    
    this.updateActiveStates();
    setTimeout(() => { this.isUpdating = false; }, 100);
  }

  updateProductDOM(variant, productData, isColorChange = false) {
    // Update price
    const priceElements = this.section.querySelectorAll('[data-product-price]');
    if (priceElements.length && variant.price) {
      const formattedPrice = this.formatMoney(variant.price);
      priceElements.forEach(el => el.innerHTML = formattedPrice);
    }
    
    // Update compare price
    const comparePriceElements = this.section.querySelectorAll('[data-compare-price]');
    if (comparePriceElements.length) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const formattedComparePrice = this.formatMoney(variant.compare_at_price);
        comparePriceElements.forEach(el => {
          el.innerHTML = formattedComparePrice;
          el.style.display = '';
        });
      } else {
        comparePriceElements.forEach(el => el.style.display = 'none');
      }
    }

    // Toggle sale badge visibility based on variant sale status
    try {
      const saleBadge = this.section.querySelector('[data-sale-badge]') || document.querySelector('[data-sale-badge]');
      const productSaleEl = this.section.querySelector('.product__sale') || document.querySelector('.product__sale');
      if (saleBadge && variant) {
        let isOnSale = false;
        const saleStatusElem = document.querySelector('[data-variant-sale-status]');
        if (saleStatusElem) {
          try {
            const variantsSaleStatus = JSON.parse(saleStatusElem.textContent || '[]');
            const match = variantsSaleStatus.find(v => Number(v.variant_id) === Number(variant.id));
            if (match) isOnSale = match.on_sale === true;
          } catch(e) { /* fallback below */ }
        }
        if (typeof isOnSale !== 'boolean' || saleStatusElem == null) {
          isOnSale = !!(variant.compare_at_price && variant.price && variant.compare_at_price > variant.price);
        }
        saleBadge.classList.toggle('hidden', !isOnSale);
        if (productSaleEl) productSaleEl.classList.toggle('hidden', !isOnSale);
      }
    } catch(e) { /* no-op */ }
    
    // Update SKU
    const skuElements = this.section.querySelectorAll('[data-variant-sku]');
    if (skuElements.length && variant.sku) {
      skuElements.forEach(el => el.textContent = variant.sku);
    }
    
    // Update availability
    const formWrappers = this.section.querySelectorAll('[data-form-wrapper]');
    formWrappers.forEach(wrapper => {
      if (variant.available) {
        wrapper.classList.remove('variant--soldout', 'variant--unavailable');
      } else {
        wrapper.classList.add('variant--soldout');
        wrapper.classList.remove('variant--unavailable');
      }
    });
    
    // Update buttons
    const addToCartButtons = this.section.querySelectorAll('[data-add-to-cart]');
    addToCartButtons.forEach(btn => btn.disabled = !variant.available);
    
    const addToCartTextElements = this.section.querySelectorAll('[data-add-to-cart-text]');
    const buttonText = variant.available 
      ? (window.theme?.strings?.addToCart || 'Add to cart')
      : (window.theme?.strings?.soldOut || 'Sold out');
    addToCartTextElements.forEach(el => el.textContent = buttonText);
    
    // Update images - only when color changes
    if (isColorChange && variant.option1) {
      const colorValue = variant.option1;
      const productImages = this.section.querySelector('product-images');
      
      if (productImages) {
        // Split color value by space and get the last part (color code)
        // e.g., "Sage Stone V822" -> ["Sage", "Stone", "V822"] -> "V822"
        // or "Amber Y263" -> ["Amber", "Y263"] -> "Y263"
        const colorParts = colorValue.split(' ');
        const colorCode = colorParts[colorParts.length - 1];
        
        console.log('[Siblings] Color value:', colorValue, 'Color code:', colorCode);
        
        // Find all image slides
        const allImages = productImages.querySelectorAll('[data-type="image"]');
        let visibleCount = 0;
        
        allImages.forEach(image => {
          const imageColor = image.dataset.color;
          
          if (imageColor === colorCode) {
            // Show images with matching color
            image.classList.remove('media--hidden');
            image.style.display = '';
            visibleCount++;
          } else {
            // Hide all other images
            image.classList.add('media--hidden');
            image.style.display = 'none';
          }
        });
        
        console.log('[Siblings] Showing', visibleCount, 'images for color code:', colorCode);
        
        // Make the first visible image active
        const firstVisible = productImages.querySelector(`[data-type="image"][data-color="${colorCode}"]`);
        if (firstVisible) {
          firstVisible.classList.add('media--active');
          if (firstVisible.dataset.mediaId) {
            productImages.dataset.activeMedia = firstVisible.dataset.mediaId;
          }
        }
      }
    }
    
    // Update legend values (color and size) to reflect current selection
    try {
      // Update color legend (current product block)
      if (variant.option1) {
        const colorLegendEl = this.container.querySelector('[data-current-product] .radio__legend [data-option-value]');
        if (colorLegendEl) {
          const colorValue = typeof variant.option1 === 'string' ? variant.option1.trim() : '';
          const parts = colorValue.split(' ');
          const displayColor = parts.length > 1 ? parts.slice(0, -1).join(' ') : colorValue;
          colorLegendEl.textContent = displayColor;
        }
      }

      // Update size legend
      if (variant.option2) {
        const sizeLegendEl = this.section.querySelector('[data-sibling-sizes] .radio__legend [data-option-value]');
        if (sizeLegendEl) {
          const sizeValue = typeof variant.option2 === 'string' ? variant.option2.trim() : '';
          const sp = sizeValue.split(' ');
          const displaySize = sp.length > 1 ? sp.slice(0, -1).join(' ') : sizeValue;
          sizeLegendEl.textContent = displaySize;
        }
      }
    } catch(e) { /* no-op */ }

    // Update size availability - only when color changes
    if (isColorChange) {
      this.updateSizeAvailability(variant, productData);
    }
  }

  updateSizeAvailability(selectedVariant, productData) {
    const selectedColor = selectedVariant.option1;
    const sizeButtons = this.section.querySelectorAll('[data-sibling-sizes] .radio__button');
    
    console.log('[Siblings] === SIZE AVAILABILITY DEBUG ===');
    console.log('[Siblings] Selected color:', selectedColor);
    console.log('[Siblings] Total variants in product:', productData.variants.length);
    
    // Debug: show all variants for this product
    const colorVariants = productData.variants.filter(v => v.option1 === selectedColor);
    console.log('[Siblings] Variants matching color "' + selectedColor + '":', colorVariants.length);
    colorVariants.forEach(v => {
      console.log('[Siblings]   - Size:', v.option2, '| Available:', v.available, '| ID:', v.id);
    });
    
    let enabledCount = 0;
    let currentSizeAvailable = false;
    let firstAvailableInput = null;
    const currentlyCheckedInput = this.section.querySelector('[data-sibling-sizes] [data-single-option-selector]:checked');
    const currentSize = currentlyCheckedInput?.value;
    
    sizeButtons.forEach(button => {
      const input = button.querySelector('[data-single-option-selector]');
      if (!input) return;
      
      const size = input.value;
      
      // Find the variant with this color + size combination
      const matchingVariant = productData.variants.find(v => 
        v.option1 === selectedColor && v.option2 === size
      );
      
      console.log('[Siblings] Checking size:', size);
      console.log('[Siblings]   - Found variant?', !!matchingVariant);
      if (matchingVariant) {
        console.log('[Siblings]   - Variant ID:', matchingVariant.id);
        console.log('[Siblings]   - Available property:', matchingVariant.available);
        console.log('[Siblings]   - Variant full data:', matchingVariant);
      }
      
      if (matchingVariant) {
        // Variant exists for this color + size combination
        button.style.display = '';
        
        if (matchingVariant.available) {
          // Size has stock - enable it
          input.disabled = false;
          input.classList.remove('unavailable');
          input.classList.remove('sold-out');
          enabledCount++;
          
          // Track if current size is available
          if (size === currentSize) {
            currentSizeAvailable = true;
          }
          
          // Track first available size
          if (!firstAvailableInput) {
            firstAvailableInput = input;
          }
          
          console.log('[Siblings] ✓ Size', size, 'ENABLED (in stock)');
        } else {
          // Size exists but sold out - show but disable
          input.disabled = true;
          input.classList.add('unavailable');
          input.classList.add('sold-out');
          console.log('[Siblings] ✗ Size', size, 'DISABLED (sold out)');
        }
      } else {
        // No variant exists for this color + size - hide completely
        button.style.display = 'none';
        input.disabled = true;
        input.classList.add('unavailable');
        console.log('[Siblings] − Size', size, 'HIDDEN (no variant for this color)');
      }
    });
    
    console.log('[Siblings] === SUMMARY: ' + enabledCount + ' sizes enabled ===');
    
    // If current size is not available, select the first available size
    if (!currentSizeAvailable && firstAvailableInput) {
      console.log('[Siblings] Current size "' + currentSize + '" not available, switching to "' + firstAvailableInput.value + '"');
      firstAvailableInput.checked = true;
      
      // Find the new variant with the new size
      const newVariant = productData.variants.find(v => 
        v.option1 === selectedColor && v.option2 === firstAvailableInput.value
      );
      
      if (newVariant) {
        // Update variant ID input
        const variantIdInput = this.section.querySelector('input[name="id"]');
        if (variantIdInput) {
          variantIdInput.value = newVariant.id;
        }
        
        // Update URL
        if (window.history.replaceState) {
          const newUrl = this.getUrlWithVariant(window.location.href, newVariant.id);
          window.history.replaceState({path: newUrl}, '', newUrl);
        }
        
        // Update product info but NOT size availability (we just did that)
        this.updateProductDOM(newVariant, productData, false);
      }
    } else if (currentSizeAvailable) {
      console.log('[Siblings] Current size "' + currentSize + '" is still available');
    } else if (!firstAvailableInput) {
      console.log('[Siblings] No available sizes for this color');
    }
  }

  findSiblingVariant(siblingProductId, colorValue) {
    const siblingWrapper = this.container.querySelector(`[data-sibling-product="${siblingProductId}"]`);
    if (!siblingWrapper) return null;
    
    const siblingDataElement = siblingWrapper.querySelector('[data-sibling-product-json]');
    if (!siblingDataElement) return null;
    
    const siblingProductData = JSON.parse(siblingDataElement.textContent);
    
    // Get current size
    const sizeSelector = this.section.querySelector('[data-sibling-sizes] [data-single-option-selector]:checked') ||
                        this.section.querySelector('[data-sibling-sizes] [data-single-option-selector][type="hidden"]');
    const currentSize = sizeSelector?.value;
    
    // Find matching variant with color + size
    let matchingVariant = siblingProductData.variants.find(v =>
      v.option1 === colorValue && v.option2 === currentSize
    );
    
    // Fallback: find any variant with the color
    if (!matchingVariant) {
      matchingVariant = siblingProductData.variants.find(v => v.option1 === colorValue);
    }
    
    return matchingVariant?.id;
  }

  updateActiveStates() {
    const currentProduct = this.container.querySelector('[data-current-product]');
    if (!currentProduct) return;
    
    const swatches = currentProduct.querySelectorAll('[data-sibling-color-swatch]');
    swatches.forEach(swatch => {
      const input = swatch.querySelector('input[data-sibling-color-input]');
      if (input?.checked) {
        swatch.classList.add('swatch__button--selected');
        swatch.setAttribute('aria-current', 'true');
      } else {
        swatch.classList.remove('swatch__button--selected');
        swatch.removeAttribute('aria-current');
      }
    });
  }

  getUrlWithVariant(url, variantId) {
    if (!url || !variantId) return url;
    if (/variant=/.test(url)) {
      return url.replace(/(variant=)[^&]+/, '$1' + variantId);
    } else if (/\?/.test(url)) {
      return url.concat('&variant=').concat(variantId);
    }
    return url.concat('?variant=').concat(variantId);
  }

  hideUnavailableColors() {
    // Check each sibling product's color swatches
    const siblingWrappers = this.container.querySelectorAll('[data-sibling-product]');
    
    siblingWrappers.forEach(wrapper => {
      const isCurrentProduct = wrapper.hasAttribute('data-current-product');
      const productId = wrapper.dataset.siblingProduct;
      
      // Get product data
      let productData = null;
      
      if (isCurrentProduct) {
        const productDataElement = document.querySelector('[data-product-json]');
        if (productDataElement) {
          productData = JSON.parse(productDataElement.textContent);
        }
      } else {
        const siblingDataElement = wrapper.querySelector('[data-sibling-product-json]');
        if (siblingDataElement) {
          productData = JSON.parse(siblingDataElement.textContent);
        }
      }
      
      if (!productData) return;
      
      // Check each color swatch in this product
      const colorSwatches = wrapper.querySelectorAll('[data-sibling-color-swatch]');
      
      colorSwatches.forEach(swatch => {
        const colorValue = swatch.dataset.colorValue;
        
        // Check if any variant with this color is available
        const hasAvailableVariant = productData.variants.some(variant => {
          return variant.option1 === colorValue && variant.available;
        });
        
        if (!hasAvailableVariant) {
          // Hide the swatch and disable the input
          swatch.style.display = 'none';
          const input = swatch.querySelector('input[data-sibling-color-input]');
          if (input) {
            input.disabled = true;
          }
          console.log('[Siblings] Hiding color swatch with no available variants:', colorValue, 'for product:', productId);
        }
      });
    });
  }

  redirectToAvailableVariant() {
    // Get product data
    const productDataElement = document.querySelector('[data-product-json]');
    if (!productDataElement) return;
    
    const productData = JSON.parse(productDataElement.textContent);
    
    // Get current variant ID from URL or input
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');
    const variantIdInput = this.section.querySelector('input[name="id"]');
    const currentVariantId = urlVariantId || variantIdInput?.value;
    
    if (!currentVariantId) {
      // No variant selected, redirect to first available
      const firstAvailable = productData.variants.find(v => v.available);
      if (firstAvailable) {
        console.log('[Siblings] No variant selected, redirecting to first available:', firstAvailable.id);
        const newUrl = this.getUrlWithVariant(window.location.href, firstAvailable.id);
        window.location.href = newUrl;
      }
      return;
    }
    
    // Find the current variant
    const currentVariant = productData.variants.find(v => v.id.toString() === currentVariantId.toString());
    
    if (!currentVariant) {
      console.log('[Siblings] Current variant not found');
      return;
    }
    
    // Check if current variant is available
    if (!currentVariant.available) {
      console.log('[Siblings] Current variant', currentVariantId, 'is not available, finding first available variant');
      
      // Try to find an available variant with the same size
      const currentSize = currentVariant.option2;
      let firstAvailable = null;
      
      if (currentSize) {
        // Try same size, different color
        firstAvailable = productData.variants.find(v => 
          v.available && v.option2 === currentSize
        );
      }
      
      // If no variant with same size, just get first available
      if (!firstAvailable) {
        firstAvailable = productData.variants.find(v => v.available);
      }
      
      if (firstAvailable) {
        console.log('[Siblings] Redirecting to available variant:', firstAvailable.id, '(', firstAvailable.option1, '/', firstAvailable.option2, ')');
        const newUrl = this.getUrlWithVariant(window.location.href, firstAvailable.id);
        window.location.href = newUrl;
      } else {
        console.log('[Siblings] No available variants found for this product');
      }
    } else {
      console.log('[Siblings] Current variant', currentVariantId, 'is available');
    }
  }

  updateInitialSizeAvailability() {
    // Get product data
    const productDataElement = document.querySelector('[data-product-json]');
    if (!productDataElement) return;
    
    const productData = JSON.parse(productDataElement.textContent);
    
    // Get current variant ID from URL or input
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');
    const variantIdInput = this.section.querySelector('input[name="id"]');
    const currentVariantId = urlVariantId || variantIdInput?.value;
    
    if (!currentVariantId) return;
    
    // Find the current variant
    const currentVariant = productData.variants.find(v => v.id.toString() === currentVariantId.toString());
    
    if (!currentVariant) return;
    
    console.log('[Siblings] Updating initial size availability for variant:', currentVariantId, '(', currentVariant.option1, '/', currentVariant.option2, ')');
    
    // Update size availability based on the current color
    this.updateSizeAvailability(currentVariant, productData);
  }

  updateInitialVariantUI() {
    try {
      const productDataElement = document.querySelector('[data-product-json]');
      if (!productDataElement) return;
      const productData = JSON.parse(productDataElement.textContent);
      if (!productData || !Array.isArray(productData.variants)) return;

      // Determine current variant ID from URL or hidden input
      const urlParams = new URLSearchParams(window.location.search);
      const urlVariantId = urlParams.get('variant');
      const variantIdInput = this.section.querySelector('input[name="id"]');
      const currentVariantId = urlVariantId || variantIdInput?.value;
      if (!currentVariantId) return;

      const currentVariant = productData.variants.find(v => v.id.toString() === currentVariantId.toString());
      if (!currentVariant) return;

      // Ensure matching color swatch is checked for current product
      const currentColor = currentVariant.option1;
      if (currentColor) {
        const swatches = this.container.querySelectorAll('[data-current-product] [data-sibling-color-swatch] input[data-sibling-color-input]');
        swatches.forEach(input => {
          if (input.value === currentColor) {
            input.checked = true;
          }
        });
        this.updateActiveStates();
      }

      // Update legends and images to match current variant
      this.updateProductDOM(currentVariant, productData, true);
    } catch(e) { /* no-op */ }
  }

  formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = parseFloat(cents) / 100;
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.theme?.currency || 'USD',
      minimumFractionDigits: 2
    });
    return formatter.format(value);
  }
}

// Initialize
function initProductSiblingsVariants() {
  const containers = document.querySelectorAll('[data-product-siblings-variants]');
  containers.forEach(container => {
    new ProductSiblingsVariants(container);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductSiblingsVariants);
} else {
  initProductSiblingsVariants();
}

// Re-initialize on Shopify section re-render (theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    initProductSiblingsVariants();
  });
}
